import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import LegalFootnote from './components/LegalFootnote';
import './portal-v2.css';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Public "Pay Entire Balance" page for a statement — pays all of a contact's
 * (or firm's) open invoices in one lump sum. No account required.
 */
const ClientV2StatementPayment = () => {
  const { token } = useParams();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => { document.title = 'Pay Statement | WesTex Legal'; }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/guest/statement/${token}`);
      if (res.ok) {
        const d = await res.json();
        setStmt(d);
        if (d.paid) setPaid(true);
      } else {
        const t = await res.text();
        setError(t || `Could not load statement (${res.status})`);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [API_URL, token]);

  useEffect(() => { load(); }, [load]);

  // 3DS redirect-back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect_status') === 'succeeded') setPaid(true);
  }, []);

  const createIntent = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/guest/statement/${token}/pay-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || 'Could not start payment');
    }
    return await res.json();
  }, [API_URL, token]);

  const confirmPayment = useCallback(async (pi) => {
    try {
      await fetch(`${API_URL}/api/guest/statement/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: pi?.id }),
      });
    } catch { /* webhook/poll will reconcile */ }
    setShowPay(false);
    setPaid(true);
  }, [API_URL, token]);

  const total = Number(stmt?.total_amount || 0);

  return (
    <div className="portal-v2">
      <div className="pv2-login-shell">
        <div className="pv2-login-card" data-testid="pv2-statement-pay-card" style={{ maxWidth: 560 }}>
          <div className="pv2-mono-tag" style={{ marginBottom: 14 }}>Secure Payment · WesTex Legal</div>
          <h1 className="pv2-display" style={{ fontSize: 36, marginBottom: 8 }}>
            Pay your <span className="pv2-display-italic">statement.</span>
          </h1>

          {loading && <p style={{ color: 'var(--pv2-text-muted)' }}>Loading statement…</p>}
          {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

          {!loading && stmt && paid && (
            <div className="pv2-alert pv2-alert-green" data-testid="pv2-statement-paid"><CheckCircle size={14} /><span>Your balance has been paid in full. Thank you!</span></div>
          )}

          {!loading && stmt && !paid && (
            <>
              <div style={{ padding: 20, background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line)', marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: 'var(--pv2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                  {stmt.invoice_count} open invoice{stmt.invoice_count === 1 ? '' : 's'}
                </div>
                {stmt.invoices.map((inv) => (
                  <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }} data-testid={`pv2-statement-inv-${inv.id}`}>
                    <span className="pv2-mono-sm">Invoice #{inv.job_number || inv.id}</span>
                    <span>{fmtUSD(inv.balance_due)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, marginTop: 6, borderTop: '1px solid var(--pv2-ink-line)', fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: 'var(--pv2-red-bright)' }}>
                  <span>Total Due</span>
                  <span data-testid="pv2-statement-total">{fmtUSD(total)}</span>
                </div>
              </div>

              <button onClick={() => { setError(null); setShowPay(true); }} className="pv2-btn pv2-btn-primary" style={{ width: '100%', padding: '16px' }} data-testid="pv2-statement-pay">
                Pay {fmtUSD(total)} now
              </button>

              <LegalFootnote action="submitting payment" testId="pv2-statement-legal" />
            </>
          )}
        </div>
      </div>

      {showPay && stmt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
              <span className="pv2-mono">Complete payment · {fmtUSD(total)}</span>
              <button onClick={() => setShowPay(false)} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-statement-pay-close">
                <X size={16} />
              </button>
            </div>
            <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }} data-testid="pv2-statement-pay-modal">
              <EmbeddedStripePaymentElement
                createIntent={createIntent}
                returnUrl={`${window.location.origin}/pay-statement/${token}?redirect_status=succeeded`}
                onSuccess={confirmPayment}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientV2StatementPayment;
